import { DRXError } from '@/lib/drx-error';
import { startCartCheckout } from '@/lib/cart-checkout';
import { functions } from '@/lib/firebase';
import { CartUseCase } from '../usecases/cart.usecase';
import { AnalyticsUseCase } from '../usecases/analytics.usecase';
import { UserRepository } from '../repository/user.repository';
import { CreateCartData, UpdateCartData, GetCartData, CartFilter, CreateCartItemData, GetCartItemData } from '../repository/cart.repository';

function readCartCheckoutPayload(data: unknown): {
  checkoutUrl?: string;
  sessionId?: string;
} {
  if (!data || typeof data !== 'object') return {};
  const o = data as Record<string, unknown>;
  return {
    checkoutUrl: typeof o.checkoutUrl === 'string' ? o.checkoutUrl : undefined,
    sessionId: typeof o.sessionId === 'string' ? o.sessionId : undefined,
  };
}

function readFirebaseHttpsErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const o = error as Record<string, unknown>;
  const customData = o.customData;
  if (customData && typeof customData === 'object') {
    const msg = (customData as Record<string, unknown>).message;
    if (typeof msg === 'string') return msg;
  }
  if (typeof o.details === 'string') return o.details;
  return null;
}

function readFirebaseHttpsErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const o = error as Record<string, unknown>;
  // The SDK reports callable errors as "functions/not-found"; every check
  // below compares the bare code. With the prefix, each validation refusal
  // ("no longer available", "already purchased") was treated as an internal
  // failure: the buyer was told "something went wrong" and Sentry paged us.
  if (typeof o.code === 'string') return o.code.replace(/^functions\//, '');
  return null;
}

export type UnavailableCartItem = { itemId: string; itemType: string; title: string; reason: string };

/** processCartCheckout names the items it refused in `details.unavailableItems`. */
function readUnavailableItems(error: unknown): UnavailableCartItem[] {
  const details = (error as { details?: { unavailableItems?: unknown } } | null)?.details;
  const list = details && typeof details === 'object' ? details.unavailableItems : undefined;
  return Array.isArray(list)
    ? list.filter((i): i is UnavailableCartItem => !!i && typeof i.itemId === 'string')
    : [];
}

export class CartService {
  constructor(
    private cartUseCase: CartUseCase,
    private userRepository: UserRepository,
    private analyticsUseCase?: AnalyticsUseCase
  ) {
    console.log('[CartService] Constructor - Cart use case initialized:', !!this.cartUseCase);
  }

  // ===== CART COLLECTION VALIDATION =====

  /**
   * Ensure cart data is stored in carts collection, not user document
   * This method can be called to migrate any legacy cart data from user docs
   */
  async validateCartDataLocation(userId: string): Promise<{
    isValid: boolean;
    message: string;
    migrated?: boolean;
  }> {
    console.log('[CartService.validateCartDataLocation] START - UserId:', userId);
    
    try {
      // Get user document to check for any cart-related fields
      const user = await this.userRepository.getUserById(userId);
      if (!user) {
        return { isValid: false, message: 'User not found' };
      }

      // Check for legacy cart fields in user document
      const legacyCartFields = [
        'cart', 'cartItems', 'shoppingCart', 'activeCart', 
        'cartId', 'currentCart', 'cartData', 'cartItems'
      ];
      
      const foundLegacyFields = legacyCartFields.filter(field => 
        user.hasOwnProperty(field) && user[field] !== null && user[field] !== undefined
      );

      if (foundLegacyFields.length > 0) {
        console.warn('[CartService.validateCartDataLocation] Found legacy cart fields in user document:', foundLegacyFields);
        
        // TODO: Could implement migration logic here if needed
        // For now, just warn about the issue
        return {
          isValid: false,
          message: `User document contains cart data in fields: ${foundLegacyFields.join(', ')}. Cart data should be in carts collection.`,
          migrated: false
        };
      }

      // Verify user has proper cart in carts collection
      const cart = await this.cartUseCase.getUserActiveCart(userId);
      const hasCartInCollection = cart !== null;

      console.log('[CartService.validateCartDataLocation] SUCCESS - Cart data properly located in carts collection');
      return {
        isValid: true,
        message: hasCartInCollection 
          ? 'Cart data properly stored in carts collection'
          : 'No active cart found (this is normal for users who haven\'t added items yet)'
      };

    } catch (error) {
      console.error('[CartService.validateCartDataLocation] ERROR:', error);
      return {
        isValid: false,
        message: `Error validating cart data location: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // ===== AUTHENTICATION VALIDATION =====
  
  /**
   * Validate that user is authenticated and not a guest/temp user
   */
  private validateAuthenticatedUser(userId: string): void {
    if (!userId) {
      throw new DRXError({ code: 'CART_AUTH_REQUIRED', devMessage: 'Cart used without an authenticated user', userMessage: 'Please sign in to use your cart.' });
    }
    
    if (userId.startsWith('temp_') || userId.startsWith('guest_')) {
      throw new DRXError({ code: 'CART_REGISTERED_ONLY', devMessage: 'Cart used by an unregistered user', userMessage: 'Create an account or sign in to use your cart.' });
    }
  }

  // ===== CART CHECKOUT WITH STRIPE =====


  /**
   * Create Stripe checkout session for cart items
   * This handles curriculum resource purchases through cart
   */
  async createCartCheckout(cartData: {
    userId: string;
    items?: any[]; // Optional: pass items directly or get from user's cart
    email?: string;
    couponCode?: string;
  }): Promise<{
    success: boolean;
    checkoutUrl?: string;
    sessionId?: string;
    error?: string;
    code?: string;
    unavailableItems?: UnavailableCartItem[];
  }> {
    console.log('[CartService.createCartCheckout] START - CartData:', cartData);
    
    try {
      // Validate authenticated user
      this.validateAuthenticatedUser(cartData.userId);
      // Step 1: Get cart items (either passed directly or from user's cart)
      let cartItems = cartData.items;
      
      if (!cartItems || cartItems.length === 0) {
        console.log('[CartService.createCartCheckout] Getting cart items from carts collection');
        const userCartData = await this.getUserCartWithItems(cartData.userId);
        if (!userCartData || !userCartData.items || userCartData.items.length === 0) {
          throw new DRXError({ code: 'CART_EMPTY_OR_MISSING', devMessage: 'No cart document or no items for user', context: { userId: cartData.userId } });
        }
        cartItems = userCartData.items;
      }

      // Step 2: Validate cart items. Courses sell on the same rails as
      // curriculum (see hooks/use-buy-course), so a cart may hold either.
      // This filtered to `resource` only, which silently dropped every course
      // line and then failed the whole checkout as "No valid curriculum
      // resources in cart" — paid courses could not be bought at all.
      const validItems = cartItems.filter(item =>
        (item.itemType === 'resource' || item.itemType === 'course') && item.price > 0
      );

      if (validItems.length === 0) {
        throw new DRXError({ code: 'CART_NO_PURCHASABLE_ITEMS', devMessage: 'Cart held no resource or course line with a price above zero', context: { userId: cartData.userId, itemTypes: cartItems.map(i => i.itemType) }, userMessage: 'Nothing in your cart can be purchased right now.' });
      }

      // Step 3: Use the deployed cart checkout flow that matches payment success fulfillment
      console.log('[CartService.createCartCheckout] Creating Stripe checkout session');
      
      const checkoutData = {
        items: validItems.map(item => ({
          title: item.title,
          price: item.price,
          quantity: item.quantity || 1,
          itemId: item.itemId,
          itemType: item.itemType
        })),
        userId: cartData.userId,
        email: cartData.email,
        couponCode: cartData.couponCode,
        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
      };
      
      console.log('[CartService.createCartCheckout] Checkout data being sent to cloud function:', JSON.stringify(checkoutData, null, 2));
      const totalAmount = checkoutData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      console.log('[CartService.createCartCheckout] Calculated total amount:', totalAmount);

      const checkoutResult = await startCartCheckout(functions, checkoutData);
      const checkoutResponse = readCartCheckoutPayload(checkoutResult.data);

      if (!checkoutResponse.checkoutUrl) {
        throw new DRXError({ code: 'CART_CHECKOUT_SESSION_FAILED', devMessage: 'createCartCheckout returned no checkout URL', context: { userId: cartData.userId } });
      }

      // Step 4: Track analytics (temporarily disabled)
      console.log('[CartService.createCartCheckout] Analytics tracking temporarily disabled');
      // TODO: Re-enable analytics after fixing undefined value issues

      console.log('[CartService.createCartCheckout] SUCCESS - Checkout URL:', checkoutResponse.checkoutUrl);
      return {
        success: true,
        checkoutUrl: checkoutResponse.checkoutUrl,
        sessionId: checkoutResponse.sessionId
      };

    } catch (error) {
      const httpsMessage = readFirebaseHttpsErrorMessage(error);
      const httpsCode = readFirebaseHttpsErrorCode(error);
      // Validation codes are user-facing (cart empty, item below minimum,
      // duplicate purchase, etc). Demote to warn so they don't pollute
      // Sentry. The cart-page already shows the message in a toast.
      const validationCodes = new Set([
        'failed-precondition',
        'invalid-argument',
        'already-exists',
        'not-found',
      ]);
      if (httpsCode && validationCodes.has(httpsCode)) {
        console.warn('[CartService.createCartCheckout] validation:', httpsCode, httpsMessage);
      } else {
        console.error('[CartService.createCartCheckout] ERROR:', error);
      }

      return {
        success: false,
        error: httpsMessage || (error instanceof Error ? error.message : 'Unknown error occurred'),
        code: httpsCode || undefined,
        unavailableItems: readUnavailableItems(error),
      };
    }
  }

  /**
   * Handle successful cart purchase after payment
   * Called from success page with session_id from Stripe
   */
  async handleSuccessfulCartPurchase(sessionId: string, userId: string): Promise<{
    success: boolean;
    purchases?: any[];
    error?: string;
  }> {
    console.log('[CartService.handleSuccessfulCartPurchase] START - SessionId:', sessionId, 'UserId:', userId);
    
    try {
      // Step 1: Get purchase records by session ID
      // This would typically be handled by the webhook, but we can also verify here
      const purchases = await this.getPurchasesBySessionId(sessionId);
      
      // Step 2: Fulfill resource purchases - Update user and resource documents
      console.log('[CartService.handleSuccessfulCartPurchase] Fulfilling resource purchases');
      await this.fulfillResourcePurchases(userId, purchases);
      
      // Step 3: Clear user's cart after successful purchase
      console.log('[CartService.handleSuccessfulCartPurchase] Clearing user cart');
      await this.clearUserCart(userId);

      // Step 4: Track successful purchase
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackActivity(
          userId,
          'cartPurchaseCompleted',
          {
            session_id: sessionId,
            purchasesCount: purchases.length,
            totalAmount: purchases.reduce((sum, p) => sum + p.amount, 0)
          }
        );
      }

      console.log('[CartService.handleSuccessfulCartPurchase] SUCCESS - Purchases:', purchases.length);
      return {
        success: true,
        purchases
      };

    } catch (error) {
      console.error('[CartService.handleSuccessfulCartPurchase] ERROR:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Fulfill resource purchases by updating user and resource documents
   * Updates user document with purchased resources and resource documents with buyer info
   */
  private async fulfillResourcePurchases(userId: string, purchases: any[]): Promise<void> {
    console.log('[CartService.fulfillResourcePurchases] START - UserId:', userId, 'Purchases:', purchases.length);
    
    try {
      // Get user information for resource updates
      const user = await this.userRepository.getUserById(userId);
      if (!user) {
        throw new DRXError({ code: 'CART_FULFILMENT_USER_MISSING', devMessage: 'Purchase fulfilment could not resolve the buyer' });
      }

      // Collect all resource IDs from all purchases
      const resourceIds = new Set<string>();
      const purchaseItems: Array<{
        resourceId: string;
        purchaseId: string;
        purchaseDate: Date;
        amount: number;
        title?: string;
      }> = [];

      // Extract resource IDs from purchase items
      for (const purchase of purchases) {
        const items = purchase.items || [];
        for (const item of items) {
          // Was `'resource' || 'resource'` — the same test twice, so courses never
          // matched and fell through silently.
          if (item.itemType === 'resource' || item.itemType === 'course') {
            const resourceId = item.itemId || item.itemId || item.resourceId || item.resourceId;
            if (resourceId) {
              resourceIds.add(resourceId);
              purchaseItems.push({
                resourceId,
                purchaseId: purchase.id,
                purchaseDate: purchase.createdAt?.toDate?.() || new Date(),
                amount: item.price || item.amount || 0,
                title: item.title || item.name
              });
            }
          }
        }
      }

      console.log('[CartService.fulfillResourcePurchases] Found resources to fulfill:', Array.from(resourceIds));

      // Step 1: Update user document with purchased resources
      await this.updateUserPurchasedResources(userId, user, purchaseItems);

      // Step 2: Update each resource document with buyer information
      await this.updateResourceBuyers(Array.from(resourceIds), user, purchaseItems);

      console.log('[CartService.fulfillResourcePurchases] SUCCESS - All resources fulfilled');

    } catch (error) {
      console.error('[CartService.fulfillResourcePurchases] ERROR:', error);
      // Don't throw here - we don't want to fail the entire purchase flow
      // The purchase is already completed, this is just fulfillment
      console.warn('[CartService.fulfillResourcePurchases] Purchase fulfillment failed, but purchase is still valid');
    }
  }

  /**
   * Update user document to include purchased resources
   */
  private async updateUserPurchasedResources(
    userId: string, 
    user: any, 
    purchaseItems: Array<{ resourceId: string; purchaseId: string; purchaseDate: Date; amount: number; title?: string; }>
  ): Promise<void> {
    console.log('[CartService.updateUserPurchasedResources] START - UserId:', userId, 'Items:', purchaseItems.length);
    
    try {
      // Get current purchased resources from user document
      const currentPurchasedResources = user.purchasedResources || [];
      
      // Create new resource entries
      const newResourceEntries = purchaseItems.map(item => ({
        resourceId: item.resourceId,
        purchaseId: item.purchaseId,
        purchasedAt: item.purchaseDate,
        amountPaid: item.amount,
        title: item.title,
        accessGranted: true,
        downloadCount: 0,
        lastAccessed: null
      }));

      // Merge with existing resources (avoid duplicates)
      const existingResourceIds = new Set(currentPurchasedResources.map((r: any) => r.resourceId));
      const uniqueNewEntries = newResourceEntries.filter(entry => !existingResourceIds.has(entry.resourceId));
      
      const updatedPurchasedResources = [...currentPurchasedResources, ...uniqueNewEntries];

      // Update user document
      await this.userRepository.updateUser(userId, {
        purchasedResources: updatedPurchasedResources,
        totalResourcePurchases: (user.totalResourcePurchases || 0) + uniqueNewEntries.length,
        lastPurchaseDate: new Date(),
        updatedAt: new Date()
      });

      console.log('[CartService.updateUserPurchasedResources] SUCCESS - Added', uniqueNewEntries.length, 'new resources to user');

    } catch (error) {
      console.error('[CartService.updateUserPurchasedResources] ERROR:', error);
      throw new DRXError({ code: 'CART_PURCHASED_RESOURCES_UPDATE_FAILED', devMessage: 'Could not record purchased resources on the user' });
    }
  }

  /**
   * Update resource documents to include buyer information
   */
  private async updateResourceBuyers(
    resourceIds: string[], 
    user: any, 
    purchaseItems: Array<{ resourceId: string; purchaseId: string; purchaseDate: Date; amount: number; title?: string; }>
  ): Promise<void> {
    console.log('[CartService.updateResourceBuyers] START - ResourceIds:', resourceIds.length, 'User:', user.email);
    
    try {
      // Get ResourcesService to access resource repository
      const { default: services } = await import('@/lib/services');
      
      // Update each resource with buyer information
      for (const resourceId of resourceIds) {
        try {
          console.log('[CartService.updateResourceBuyers] Updating resource:', resourceId);
          
          // Get current resource data
          const resource = await services.resources.getResourceById(resourceId);
          if (!resource) {
            console.warn('[CartService.updateResourceBuyers] Resource not found:', resourceId);
            continue;
          }

          // Find purchase info for this resource
          const purchaseInfo = purchaseItems.find(item => item.resourceId === resourceId);
          if (!purchaseInfo) {
            console.warn('[CartService.updateResourceBuyers] No purchase info found for resource:', resourceId);
            continue;
          }

          // Get current buyers list
          const currentBuyers = resource.buyers || [];
          
          // Check if user already exists in buyers list
          const existingBuyerIndex = currentBuyers.findIndex((buyer: any) => buyer.userId === user.id || buyer.userId === user.id);
          
          const buyerData = {
            userId: user.id || user.id,
            email: user.email,
            name: user.displayName || user.name || user.firstName + ' ' + user.lastName || 'Anonymous',
            purchaseId: purchaseInfo.purchaseId,
            purchasedAt: purchaseInfo.purchaseDate,
            amountPaid: purchaseInfo.amount,
            accessGranted: true,
            downloadCount: 0,
            lastAccessed: null
          };

          let updatedBuyers;
          if (existingBuyerIndex >= 0) {
            // Update existing buyer entry
            updatedBuyers = [...currentBuyers];
            updatedBuyers[existingBuyerIndex] = { ...updatedBuyers[existingBuyerIndex], ...buyerData };
          } else {
            // Add new buyer entry
            updatedBuyers = [...currentBuyers, buyerData];
          }

          // Update resource document
          await services.resources.updateResource(resourceId, {
            buyers: updatedBuyers,
            totalPurchases: (resource.totalPurchases || 0) + (existingBuyerIndex >= 0 ? 0 : 1),
            totalRevenue: (resource.totalRevenue || 0) + purchaseInfo.amount,
            lastPurchaseDate: purchaseInfo.purchaseDate,
            updatedAt: new Date()
          });

          console.log('[CartService.updateResourceBuyers] SUCCESS - Updated resource:', resourceId);

        } catch (resourceError) {
          console.error('[CartService.updateResourceBuyers] ERROR updating resource:', resourceId, resourceError);
          // Continue with other resources even if one fails
        }
      }

      console.log('[CartService.updateResourceBuyers] SUCCESS - Updated all resources');

    } catch (error) {
      console.error('[CartService.updateResourceBuyers] ERROR:', error);
      throw new DRXError({ code: 'CART_RESOURCE_BUYERS_UPDATE_FAILED', devMessage: 'Could not record the buyer on the resource' });
    }
  }

  // ===== CART MANAGEMENT =====

  async createCart(cartData: CreateCartData): Promise<GetCartData> {
    console.log('[CartService.createCart] START - CartData:', cartData);
    try {
      const cart = await this.cartUseCase.createCart(cartData);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'cartCreated',
          eventData: {
            cartId: cart.id,
            userId: cart.userId,
            initialAmount: cart.totalAmount,
            currency: cart.currency
          },
          userId: cart.userId
        });
      }

      console.log('[CartService.createCart] SUCCESS - Cart created:', cart.id);
      return cart;
    } catch (error) {
      console.error('[CartService.createCart] ERROR:', error);
      throw error;
    }
  }

  async getCartById(cartId: string): Promise<GetCartData | null> {
    console.log('[CartService.getCartById] START - CartId:', cartId);
    try {
      const cart = await this.cartUseCase.getCartById(cartId);
      console.log('[CartService.getCartById] SUCCESS - Cart found:', !!cart);
      return cart;
    } catch (error) {
      console.error('[CartService.getCartById] ERROR:', error);
      throw error;
    }
  }

  async getCarts(filter: CartFilter = {}, limitCount?: number): Promise<GetCartData[]> {
    console.log('[CartService.getCarts] START - Filter:', filter, 'Limit:', limitCount);
    try {
      const carts = await this.cartUseCase.getCarts(filter, limitCount);
      console.log('[CartService.getCarts] SUCCESS - Retrieved', carts.length, 'carts');
      return carts;
    } catch (error) {
      console.error('[CartService.getCarts] ERROR:', error);
      throw error;
    }
  }

  async getAllCarts(limitCount?: number): Promise<GetCartData[]> {
    console.log('[CartService.getAllCarts] START - Limit:', limitCount);
    try {
      const carts = await this.cartUseCase.getAllCarts(limitCount);
      console.log('[CartService.getAllCarts] SUCCESS - Retrieved', carts.length, 'carts');
      return carts;
    } catch (error) {
      console.error('[CartService.getAllCarts] ERROR:', error);
      throw error;
    }
  }

  async updateCart(cartId: string, updateData: UpdateCartData): Promise<GetCartData> {
    console.log('[CartService.updateCart] START - CartId:', cartId, 'UpdateData:', updateData);
    try {
      const updatedCart = await this.cartUseCase.updateCart(cartId, updateData);

      // Track analytics event for significant updates
      if (this.analyticsUseCase && (updateData.status === 'completed' || updateData.status === 'abandoned')) {
        await this.analyticsUseCase.trackEvent({
          eventType: updateData.status === 'completed' ? 'cartCompleted' : 'cartAbandoned',
          eventData: {
            cartId: cartId,
            finalAmount: updatedCart.totalAmount,
            currency: updatedCart.currency
          },
          userId: updatedCart.userId
        });
      }

      console.log('[CartService.updateCart] SUCCESS - Cart updated');
      return updatedCart;
    } catch (error) {
      console.error('[CartService.updateCart] ERROR:', error);
      throw error;
    }
  }

  async deleteCart(cartId: string): Promise<void> {
    console.log('[CartService.deleteCart] START - CartId:', cartId);
    try {
      // Get cart info before deletion for analytics
      const cart = await this.cartUseCase.getCartById(cartId);
      
      await this.cartUseCase.deleteCart(cartId);

      // Track analytics event
      if (this.analyticsUseCase && cart) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'cartDeleted',
          eventData: {
            cartId: cartId,
            finalAmount: cart.totalAmount,
            currency: cart.currency
          },
          userId: cart.userId
        });
      }

      console.log('[CartService.deleteCart] SUCCESS - Cart deleted');
    } catch (error) {
      console.error('[CartService.deleteCart] ERROR:', error);
      throw error;
    }
  }

  async getUserActiveCart(userId: string): Promise<GetCartData | null> {
    console.log('[CartService.getUserActiveCart] START - UserId:', userId);
    try {
      const activeCart = await this.cartUseCase.getUserActiveCart(userId);
      console.log('[CartService.getUserActiveCart] SUCCESS - Active cart found:', !!activeCart);
      return activeCart;
    } catch (error) {
      console.error('[CartService.getUserActiveCart] ERROR:', error);
      throw error;
    }
  }

  async getOrCreateUserCart(userId: string): Promise<GetCartData> {
    console.log('[CartService.getOrCreateUserCart] START - UserId:', userId);
    try {
      // Validate authenticated user
      this.validateAuthenticatedUser(userId);
      // Try to get existing active cart
      let cart = await this.cartUseCase.getUserActiveCart(userId);
      
      if (!cart) {
        // Create new cart if none exists
        cart = await this.cartUseCase.createCart({
          userId: userId,
          status: 'active',
          totalAmount: 0,
          currency: 'USD'
        });
      }

      console.log('[CartService.getOrCreateUserCart] SUCCESS - Cart ready:', cart.id);
      return cart;
    } catch (error) {
      console.error('[CartService.getOrCreateUserCart] ERROR:', error);
      throw error;
    }
  }

  // ===== CART ITEM MANAGEMENT =====

  async addItemToCart(userId: string, itemData: Omit<CreateCartItemData, 'cartId'>): Promise<GetCartItemData> {
    console.log('[CartService.addItemToCart] START - UserId:', userId, 'ItemData:', itemData);
    try {
      // Validate authenticated user
      this.validateAuthenticatedUser(userId);
      // Get or create user's active cart
      const cart = await this.getOrCreateUserCart(userId);

      const cartItem = await this.cartUseCase.addItemToCart(cart.id, itemData);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'itemAddedToCart',
          eventData: {
            cartId: cart.id,
            productType: itemData.productType,
            productId: itemData.productId,
            productName: itemData.productName,
            quantity: itemData.quantity,
            unitPrice: itemData.unitPrice,
            totalPrice: itemData.totalPrice
          },
          userId: userId
        });
      }

      console.log('[CartService.addItemToCart] SUCCESS - Item added to cart');
      return cartItem;
    } catch (error) {
      console.error('[CartService.addItemToCart] ERROR:', error);
      throw error;
    }
  }

  async updateCartItem(itemId: string, updateData: Partial<CreateCartItemData>): Promise<GetCartItemData> {
    console.log('[CartService.updateCartItem] START - ItemId:', itemId, 'UpdateData:', updateData);
    try {
      const updatedItem = await this.cartUseCase.updateCartItem(itemId, updateData);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'cartItemUpdated',
          eventData: {
            itemId: itemId,
            productId: updatedItem.productId,
            newQuantity: updatedItem.quantity,
            newTotal: updatedItem.totalPrice
          }
        });
      }

      console.log('[CartService.updateCartItem] SUCCESS - Cart item updated');
      return updatedItem;
    } catch (error) {
      console.error('[CartService.updateCartItem] ERROR:', error);
      throw error;
    }
  }

  async removeItemFromCart(itemId: string): Promise<void> {
    console.log('[CartService.removeItemFromCart] START - ItemId:', itemId);
    try {
      // Get item info before removal for analytics
      const cartItem = await this.cartUseCase.getCartItems(''); // This needs to be fixed in use case
      
      await this.cartUseCase.removeItemFromCart(itemId);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'itemRemovedFromCart',
          eventData: {
            itemId: itemId
          }
        });
      }

      console.log('[CartService.removeItemFromCart] SUCCESS - Item removed from cart');
    } catch (error) {
      console.error('[CartService.removeItemFromCart] ERROR:', error);
      throw error;
    }
  }

  async getCartItems(cartId: string): Promise<GetCartItemData[]> {
    console.log('[CartService.getCartItems] START - CartId:', cartId);
    try {
      const items = await this.cartUseCase.getCartItems(cartId);
      console.log('[CartService.getCartItems] SUCCESS - Retrieved', items.length, 'items');
      return items;
    } catch (error) {
      console.error('[CartService.getCartItems] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get user's active cart (from carts collection, not user document)
   */
  async getUserCart(userId: string): Promise<GetCartData | null> {
    console.log('[CartService.getUserCart] START - UserId:', userId);
    try {
      const cart = await this.cartUseCase.getUserActiveCart(userId);
      if (!cart) {
        console.log('[CartService.getUserCart] No active cart found for user:', userId);
        return null;
      }
      
      console.log('[CartService.getUserCart] SUCCESS - Found cart:', cart.id);
      return cart;
    } catch (error) {
      console.error('[CartService.getUserCart] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get user's active cart with all items (from carts collection)
   */
  async getUserCartWithItems(userId: string): Promise<{ cart: GetCartData; items: GetCartItemData[] } | null> {
    console.log('[CartService.getUserCartWithItems] START - UserId:', userId);
    try {
      // Validate authenticated user
      this.validateAuthenticatedUser(userId);
      const cart = await this.cartUseCase.getUserActiveCart(userId);
      if (!cart) {
        console.log('[CartService.getUserCartWithItems] No active cart found');
        return null;
      }

      const items = await this.cartUseCase.getCartItems(cart.id);
      
      console.log('[CartService.getUserCartWithItems] SUCCESS - Cart with', items.length, 'items');
      return { cart, items };
    } catch (error) {
      console.error('[CartService.getUserCartWithItems] ERROR:', error);
      throw error;
    }
  }

  async clearCart(cartId: string): Promise<void> {
    console.log('[CartService.clearCart] START - CartId:', cartId);
    try {
      const cart = await this.cartUseCase.getCartById(cartId);
      
      await this.cartUseCase.clearCart(cartId);

      // Track analytics event
      if (this.analyticsUseCase && cart) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'cartCleared',
          eventData: {
            cartId: cartId
          },
          userId: cart.userId
        });
      }

      console.log('[CartService.clearCart] SUCCESS - Cart cleared');
    } catch (error) {
      console.error('[CartService.clearCart] ERROR:', error);
      throw error;
    }
  }

  // ===== CART CONVERSION =====

  async prepareCartForCheckout(cartId: string): Promise<{ cart: GetCartData; items: GetCartItemData[]; total: number }> {
    console.log('[CartService.prepareCartForCheckout] START - CartId:', cartId);
    try {
      const cart = await this.cartUseCase.getCartById(cartId);
      if (!cart) {
        throw new DRXError({ code: 'CART_NOT_FOUND', devMessage: 'Cart document missing at checkout' });
      }

      if (cart.status !== 'active') {
        throw new DRXError({ code: 'CART_NOT_ACTIVE', devMessage: 'Cart status is not active at checkout' });
      }

      const items = await this.cartUseCase.getCartItems(cartId);
      if (items.length === 0) {
        throw new DRXError({ code: 'CART_EMPTY_AT_CHECKOUT', devMessage: 'Checkout attempted with zero items' });
      }

      const total = cart.totalAmount;

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'checkoutInitiated',
          eventData: {
            cartId: cartId,
            itemCount: items.length,
            totalAmount: total,
            currency: cart.currency
          },
          userId: cart.userId
        });
      }

      console.log('[CartService.prepareCartForCheckout] SUCCESS - Cart ready for checkout');
      return { cart, items, total };
    } catch (error) {
      console.error('[CartService.prepareCartForCheckout] ERROR:', error);
      throw error;
    }
  }

  async abandonCart(cartId: string): Promise<void> {
    console.log('[CartService.abandonCart] START - CartId:', cartId);
    try {
      await this.cartUseCase.abandonCart(cartId);
      console.log('[CartService.abandonCart] SUCCESS - Cart abandoned');
    } catch (error) {
      console.error('[CartService.abandonCart] ERROR:', error);
      throw error;
    }
  }

  // ===== ANALYTICS AND REPORTING =====

  async getCartAnalytics(startDate?: Date, endDate?: Date): Promise<{
    totalCarts: number;
    completedCarts: number;
    abandonedCarts: number;
    averageCartValue: number;
    conversionRate: number;
  }> {
    console.log('[CartService.getCartAnalytics] START - DateRange:', startDate, 'to', endDate);
    try {
      const filter: CartFilter = {};
      if (startDate) filter.createdAfter = startDate;
      if (endDate) filter.createdBefore = endDate;

      const allCarts = await this.cartUseCase.getCarts(filter);
      const completedCarts = allCarts.filter(cart => cart.status === 'completed');
      const abandonedCarts = allCarts.filter(cart => cart.status === 'abandoned');

      const totalCarts = allCarts.length;
      const completedCount = completedCarts.length;
      const abandonedCount = abandonedCarts.length;
      const averageCartValue = completedCount > 0 
        ? completedCarts.reduce((sum, cart) => sum + cart.totalAmount, 0) / completedCount 
        : 0;
      const conversionRate = totalCarts > 0 ? (completedCount / totalCarts) * 100 : 0;

      console.log('[CartService.getCartAnalytics] SUCCESS - Analytics calculated');
      return {
        totalCarts,
        completedCarts: completedCount,
        abandonedCarts: abandonedCount,
        averageCartValue,
        conversionRate
      };
    } catch (error) {
      console.error('[CartService.getCartAnalytics] ERROR:', error);
      throw error;
    }
  }

  // ===== PRIVATE HELPER METHODS =====

  private async getPurchasesBySessionId(sessionId: string): Promise<any[]> {
    // This would query the purchases collection by stripeSessionId
    console.log('[CartService.getPurchasesBySessionId] Getting purchases for session:', sessionId);
    
    try {
      // This is a placeholder - you'd implement the actual query
      // return await this.purchaseRepository.getBySessionId(sessionId);
      return [];
    } catch (error) {
      console.error('[CartService.getPurchasesBySessionId] ERROR:', error);
      return [];
    }
  }

  private async clearUserCart(userId: string): Promise<void> {
    console.log('[CartService.clearUserCart] Clearing cart for user:', userId);
    
    try {
      // Get user's active cart from carts collection
      const cart = await this.cartUseCase.getUserActiveCart(userId);
      
      if (cart) {
        console.log('[CartService.clearUserCart] Found active cart:', cart.id, 'clearing items');
        // Clear cart items (keep cart but remove all items)
        await this.cartUseCase.clearCart(cart.id);
        
        // Track analytics
        if (this.analyticsUseCase) {
          await this.analyticsUseCase.trackEvent({
            eventType: 'cartClearedAfterPurchase',
            eventData: {
              cartId: cart.id,
              userId: userId,
              reason: 'successfulPurchase'
            },
            userId: userId
          });
        }
        
        console.log('[CartService.clearUserCart] SUCCESS - Cart cleared for user:', userId);
      } else {
        console.log('[CartService.clearUserCart] No active cart found for user:', userId);
      }
    } catch (error) {
      console.error('[CartService.clearUserCart] ERROR:', error);
      throw error;
    }
  }
}
